import axios from 'axios';
import getApiBaseUrl from '../utils/apiBaseUrl';

const API_BASE_URL = getApiBaseUrl();
const AUTH_FAILURE_MESSAGES = new Set([
  'Невалидный или истекший токен',
  'Токен доступа не предоставлен',
  'Аккаунт неактивен'
]);

const shouldForceLogout = (error) => {
  const status = error.response?.status;
  const payload = error.response?.data || {};
  const message = payload.error || payload.message || '';

  if (status === 401) {
    return true;
  }

  if (status === 403) {
    return payload.inactive === true || AUTH_FAILURE_MESSAGES.has(message);
  }

  return false;
};

// Create an axios instance with an interceptor for adding the token
const apiClient = axios.create();

// Export apiClient for use in other components
export { apiClient };

// Interceptor for adding the token to headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor for handling authentication errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (shouldForceLogout(error)) {
      // Only auth failures should clear the session.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Sends a synchronous request to Corezoid through the backend
 * @param {Object} params - Request parameters
 * @param {string} params.processKey - Secure process key on the backend
 * @param {Object} params.data - Data to send to the process
 * @param {number} params.timeout - Response timeout in seconds
 */
export const sendCorezoidRequest = async ({ processKey, data, timeout }) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/corezoid/sync`, {
      processKey,
      data,
      timeout
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      // The server returned an error
      throw new Error(
        error.response.data?.error || 
        error.response.data?.message || 
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      // Request was sent but no response received
      throw new Error('Failed to get response from server. Check connection.');
    } else {
      // Error setting up request
      throw new Error(`Request error: ${error.message}`);
    }
  }
};

/**
 * Sends a request for actions (delete, add, update) to Corezoid through the backend (Sync API)
 * @param {Array} ops - Array of operations to send
 * @param {number} timeout - Response timeout in seconds
 */
export const sendCorezoidAction = async ({ ops, timeout = 10 }) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/corezoid/action`, {
      ops,
      timeout
    }, {
      timeout: (timeout + 5) * 1000 // Axios timeout is slightly longer than the Corezoid timeout
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.error ||
        error.response.data?.message ||
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error('Failed to get response from server. Check connection.');
    } else {
      throw new Error(`Request error: ${error.message}`);
    }
  }
};

/**
 * Checks the API configuration status
 */
export const checkApiConfig = async () => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/config`);
    return response.data;
  } catch (error) {
    console.error('Error checking configuration:', error);
    return { hasApiLogin: false, hasApiSecret: false };
  }
};

/**
 * Gets page data from Corezoid
 * @param {string} pageName - Page name (payin, payout, requisites, devices)
 * @param {Object|Array} requestData - Data to send: either a data object or an ops array
 * @param {number} timeout - Response timeout in seconds
 * @param {boolean} useSyncApi - Use Sync API (defaults to false for regular API 2.0)
 */
export const fetchPageData = async (pageName, requestData = {}, timeout = 30, useSyncApi = false) => {
  try {
    // If requestData is an array, it is an ops array
    const body = Array.isArray(requestData)
      ? { ops: requestData, timeout, useSyncApi }
      : { data: requestData, timeout, useSyncApi };

    const response = await apiClient.post(`${API_BASE_URL}/pages/${pageName}`, body);

    return response.data;
  } catch (error) {
    if (error.response) {
      const errorData = error.response.data;
      const errorMessage = errorData?.message || errorData?.error || `Server error: ${error.response.status}`;
      const fullError = errorData?.stack ? `${errorMessage}\n\n${errorData.stack}` : errorMessage;
      console.error('Server response error:', errorData);
      throw new Error(fullError);
    } else if (error.request) {
      throw new Error('Failed to get response from server. Check connection.');
    } else {
      throw new Error(`Request error: ${error.message}`);
    }
  }
};
